"use client";

import { cn } from "@/lib/utils";

interface ColorSwatchProps {
  name: string;
  hexCode: string | null;
  selected?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
  ariaLabel?: string;
}

// Fallback map for colors without hex codes
const COLOR_FALLBACKS: Record<string, string> = {
  red:          "#E84040",
  blue:         "#3B7FE8",
  "sky blue":   "#38BDF8",
  "aqua blue":  "#2DD4BF",
  green:        "#40C070",
  black:        "#1A1A1A",
  purple:       "#8B5CF6",
  pink:         "#EC4899",
  gold:         "#E8C547",
  yellow:       "#EAB308",
  white:        "#F0F0F0",
  silver:       "#94A3B8",
  "clearcoat silver": "#D0D0D0",
  "clearcoat phos bronze": "#B08D57",
  "nitro lime":     "#84CC16",
  "nitro tangerine": "#F97316",
  "multi-color":    "linear-gradient(135deg, #E84040 0%, #3B7FE8 30%, #E8C547 60%, #8B5CF6 100%)",
  "multi-colored pack": "linear-gradient(135deg, #E84040 0%, #3B7FE8 30%, #E8C547 60%, #8B5CF6 100%)",
};

function resolveColor(name: string, hexCode: string | null): string {
  if (hexCode) return hexCode;
  const lower = name.toLowerCase();
  return COLOR_FALLBACKS[lower] ?? "#3A3A3A";
}

const sizes = { sm: 14, md: 18, lg: 24 };

export function ColorSwatch({
  name,
  hexCode,
  selected,
  disabled,
  size = "md",
  onClick,
  ariaLabel,
}: ColorSwatchProps) {
  const resolved = resolveColor(name, hexCode);
  const px = sizes[size];

  const className = cn(
    "rounded-full transition-all duration-150 flex-shrink-0",
    selected ? "ring-2 ring-offset-2 ring-[#E8C547] scale-110" : "hover:scale-110",
    disabled ? "opacity-35 grayscale cursor-not-allowed hover:scale-100" : "",
    onClick && !disabled ? "cursor-pointer" : "cursor-default"
  );

  const style = {
    width: px,
    height: px,
    background: resolved,
    border: "1px solid rgba(255,255,255,0.15)",
    ringOffsetColor: "#0A0A0A",
  } as React.CSSProperties;

  // Decorative swatch (no onClick): render a non-interactive element so it can
  // sit inside a parent <button> or <a> without nesting interactive elements
  // (invalid HTML / React hydration error).
  if (!onClick) {
    return <span title={name} aria-hidden className={className} style={style} />;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={name}
      aria-label={ariaLabel ?? name}
      aria-pressed={selected}
      className={className}
      style={style}
    />
  );
}
